import { DataBaseEnum } from "./enum";

export class DATABASE {
    #DB_NAME = null;
    #DB_VERSION = null;
    #STORE_NAME = null;
    #STORAGE_EXPIRATION_TIME = null;
    #CACHE_AUTO_CHECK = false;
    constructor(DB_NAME, STORE_NAME = 'table', DB_VERSION = 1) {
        const dataBase = DataBaseEnum[DB_NAME] ?? null;

        if (dataBase === null) {
            const errorMessage = `[DATABASE] - Erro: ${DB_NAME} não encontrado em [DataBaseEnum]!`;
            console.error(errorMessage)
            throw new Error(errorMessage);
        }

        this.#STORAGE_EXPIRATION_TIME = dataBase?.cacheLife ?? null;
        if (this.#STORAGE_EXPIRATION_TIME === null) {
            const errorMessage = `[DATABASE] - Erro: ${DB_NAME} não tem conseguiu definir [STORAGE_EXPIRATION_TIME]`;
            console.error(errorMessage)
            throw new Error(errorMessage);
        }

        this.#CACHE_AUTO_CHECK = dataBase?.cacheAutoCheck ?? null;
        if (this.#CACHE_AUTO_CHECK === null) {
            const errorMessage = `[DATABASE] - Erro: ${DB_NAME} não tem conseguiu definir [CACHE_AUTO_CHECK]`;
            console.error(errorMessage)
            throw new Error(errorMessage);
        }

        this.#DB_NAME = DB_NAME;
        this.#DB_VERSION = DB_VERSION;
        this.#STORE_NAME = STORE_NAME;
    }

    // Abre ou cria o banco de dados
    #openDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.#DB_NAME, this.#DB_VERSION);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(this.#STORE_NAME)) {
                    db.createObjectStore(this.#STORE_NAME, { keyPath: "id" }); // id é string agora
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    // Adiciona um item com ID personalizado e timestamp
    async insert(id, item) {
        if (!id || typeof id !== "string") {
            throw new Error("O ID deve ser uma string válida.");
        }
        await this.#internalUpdateIntegrity();
        const db = await this.#openDB();
        const transaction = db.transaction(this.#STORE_NAME, "readwrite");
        const store = transaction.objectStore(this.#STORE_NAME);

        // Adiciona o tempo atual como string ISO
        const itemComTime = { id, content: Array.isArray(item) ? [...item][0] : item, time: new Date().toISOString() };

        return new Promise((resolve, reject) => {
            const request = store.add(itemComTime);
            request.onsuccess = () => {
                db.close(); // <- fecha o banco
                resolve(request.result);
            }
            request.onerror = (event) => {
                db.close(); // <- fecha o banco
                // if (event.target.error.name === "ConstraintError") {
                //     reject(new Error(`Erro: O ID '${id}' já existe.`));
                // } else {
                //     reject(event.target.error);
                // }
                reject(event.target.error);
            };
        });
    }
    async deleteDatabase() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.deleteDatabase(this.#DB_NAME);

            request.onsuccess = () => {
                //console.log(`[DATABASE] - Banco de dados '${this.#DB_NAME}' foi deletado com sucesso.`);
                resolve(true);
            };

            request.onerror = (event) => {
                console.error(`[DATABASE] - Erro ao deletar o banco de dados '${this.#DB_NAME}':`, event.target.error);
                reject(event.target.error);
            };

            request.onblocked = () => {
                console.warn(`[DATABASE] - O banco de dados '${this.#DB_NAME}' está bloqueado e não pode ser deletado agora.`);
            };
        });
    }
    // Obtém todos os itens
    async getAllItems() {
        const db = await this.#openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.#STORE_NAME, "readonly");
            const store = transaction.objectStore(this.#STORE_NAME);
            const request = store.getAll();

            request.onsuccess = () => {
                db.close(); // <- fecha o banco
                resolve(request.result);
            }
            request.onerror = () => {
                db.close(); // <- fecha o banco
                reject(request.error);
            }
        });
    }

    // Obtém um item pelo ID
    async getItemById(id) {
        await this.#internalUpdateIntegrity();
        const db = await this.#openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.#STORE_NAME, "readonly");
            const store = transaction.objectStore(this.#STORE_NAME);
            const request = store.get(id);

            request.onsuccess = () => {
                db.close(); // <- fecha o banco
                resolve(request.result || null);
            }
            request.onerror = () => {
                db.close(); // <- fecha o banco
                reject(request.error);
            }
        });
    }

    // Remove um item por ID
    async remove(id) {
        await this.#internalUpdateIntegrity();
        const db = await this.#openDB();
        const transaction = db.transaction(this.#STORE_NAME, "readwrite");
        const store = transaction.objectStore(this.#STORE_NAME);

        return new Promise((resolve, reject) => {
            const request = store.delete(id);
            request.onsuccess = () => {
                db.close(); // <- fecha o banco
                resolve(true);
            }
            request.onerror = () => {
                db.close(); // <- fecha o banco
                reject(request.error);
            }
        });
    }

    // Calcula a diferença de tempo entre duas datas em segundos
    timeDifference(date1, date2) {
        const difference = date1.getTime() - date2.getTime();
        return Math.abs(Math.floor(difference / 1000));
    }
    async checkCache() {
        if (this.#STORAGE_EXPIRATION_TIME === 0) return
        const bdItens = await this.getAllItems();
        bdItens.forEach(item => {
            const itemTime = new Date(item.time);
            if (this.timeDifference(new Date(), itemTime) > this.#STORAGE_EXPIRATION_TIME)
                this.remove(item.id);
        });
    }
    async #internalUpdateIntegrity() {
        if (this.#CACHE_AUTO_CHECK) { await this.checkCache() }
    }


    static async clearAllDatabases() {
        await window.indexedDB.databases().then((r) => {
            for (var i = 0; i < r.length; i++) window.indexedDB.deleteDatabase(r[i].name);
        })
    }
}
